import type { ChatroomDataObject } from '#models/services';
import { useMutation } from '#react-query';
import { setChatroomStatus } from '../../services';
import { useGetChatroomFromCache, useUpdateChatroomInCache, useUpdateChatroomStatsInCache } from '../cache';

type SetChatroomStatus = {
  chatroomId: string;
  signal?: AbortSignal;
  status: 'read' | 'unread';
};

export const useSetChatroomStatusMutation = () => {
  const getChatroomFromCache = useGetChatroomFromCache();
  const updateChatroomInCache = useUpdateChatroomInCache();
  const { adjustUnreadCount, refetchChatroomStats } = useUpdateChatroomStatsInCache();

  return useMutation<ChatroomDataObject, Error, SetChatroomStatus>({
    async mutationFn({ chatroomId, signal, status }) {
      const { data } = await setChatroomStatus({
        body: { status },
        config: signal ? { signal } : undefined,
        routeParams: { chatroomId },
      });

      return data;
    },
    async onSuccess(newChatroom, { chatroomId }) {
      const cachedChatroom = getChatroomFromCache(chatroomId);
      updateChatroomInCache({ newChatroom });

      if (!cachedChatroom?.stats) {
        await refetchChatroomStats();
        return;
      }

      const difference = (newChatroom.stats?.unread ?? 0) - cachedChatroom.stats.unread;
      adjustUnreadCount({ difference });
    },
  });
};
